import { range } from "lodash"
import * as Phaser from "phaser-ce"
import { CCUtils } from "../../../common/CCUtils"
import { COLOR } from "../../../common/Color"
import { Align, FontType } from "../../../common/TextUtils"
import { TransitionState } from "../../GameState"
import { BackgroundSprite } from "../../generic-sprites/BackgroundSprite"
import { ButtonContainer, Buttons, ButtonSize, ButtonType } from "../../generic-sprites/Buttons"
import { Fireworks } from "../../generic-sprites/Fireworks"
import { GenericAssets, loadGenericAssets } from "../../generic-sprites/GenericAssets"
import { Circle, Rectangle } from "../../generic-sprites/Shape"
import { AudioSync } from "../../utils/AudioSync"
import { colorNum, ColorUtils } from "../../utils/ColorUtils"
import { PhaserText } from "../../utils/PhaserText"
import { PhaserUtils } from "../../utils/PhaserUtils"
import { Scale } from "../../utils/Scale"
import { Sprites } from "../../utils/Sprites"
import { CampCravingsAssets, loadAssets } from "./CampCravingsAssets"
import { CampPos } from "./CampPos"
import { makeRoundOne, makeRoundTwo, Round } from "./Round"

export class CampCravingsState extends Phaser.State {

    static readonly PICNIC_1_ANIM = "picnic1"
    static readonly PICNIC_2_ANIM = "picnic2"
    static readonly LODGE_ANIM = "lodge"
    static readonly FONT_TYPE = FontType.HAND_WRITING

    private readonly largeText = 48
    private readonly mediumText = 26
    private readonly labelText = 20
    private readonly smallLabelText = 16

    private instructionsPanel: Phaser.Group

    private introAudio: Phaser.Sound
    private lake: Phaser.Sprite
    private picnic: Phaser.Sprite
    private lodge: Phaser.Sprite

    private roundText: Phaser.Text
    private roundOne: Round
    private playAgainButton: ButtonContainer
    private skipToRoundTwo?: ButtonContainer
    private skipToOutro?: ButtonContainer

    preload() {
        loadGenericAssets(this.game)
        loadAssets(this.game)
        Fireworks.loadFireworks(this.game)
    }

    async create() {
        await PhaserUtils.sharedCreateFunctions(this.game)
        ColorUtils.setBackgroundColor(this.game, COLOR.grassGreen)

        const background = new BackgroundSprite(this.game, CampCravingsAssets.BACKGROUND)
        this.game.add.existing(background)

        this.setupActivities()
        this.setupLabels()

        // Comment this in to run round one first.
        // const roundOne = makeRoundTwo(this.game, this.picnic, this.lodge, this.fireworks, () => {
        this.roundOne = makeRoundOne(this.game, this.lake, () => {
            const roundOneSummary = this.game.add.audio(CampCravingsAssets.ROUND_1_SUMMARY)
            roundOneSummary.play()

            roundOneSummary.onStop.addOnce(() => {
                this.playAgainButton.group.visible = true
            })
        })

        this.setupInstructions(this.roundOne, 1)

        this.playAgainButton = Buttons.create(
            this.game, "PLAY AGAIN", this.startRoundTwo,
            {
                x: CampPos.pickCardX(), y: CampPos.pickCardY(),
                textConfig: {type: CampCravingsState.FONT_TYPE}, buttonType: ButtonType.SECONDARY, buttonSize: ButtonSize.WIDE
            }
        )
        this.playAgainButton.group.visible = false
    }

    startRoundTwo = () => {
        this.stopIntro()
        this.roundOne.reset()
        this.setupInstructions(makeRoundTwo(this.game, this.picnic, this.lodge, () => {
            const roundTwoSummary = this.game.add.audio(CampCravingsAssets.ROUND_2_SUMMARY)
            roundTwoSummary.play()
            roundTwoSummary.onStop.addOnce(() => {
                Buttons.create(
                    this.game, "Next", () => this.game.state.start(TransitionState.CAMP_CRAVINGS_EXIT),
                    {
                        x: CampPos.pickCardX(), y: CampPos.pickCardY(), width: 0.2,
                        textConfig: {type: CampCravingsState.FONT_TYPE}, buttonType: ButtonType.SECONDARY, buttonSize: ButtonSize.WIDE
                    }
                )
            })
        }), 2)
        this.playAgainButton.group.destroy()
    }

    setupInstructions = (round: Round, roundNum: number) => {

        this.introAudio = this.game.add.audio(roundNum === 1 ? CampCravingsAssets.INTRO : CampCravingsAssets.ROUND_2_INTRO)
        const oneOffs = new AudioSync(this.game, this.introAudio)

        this.roundText.text = `ROUND ${roundNum}`

        const tint = Sprites.add(this.game, GenericAssets.BLACK_PIXEL)
        tint.width = Scale.width()
        tint.height = Scale.height()
        tint.alpha = 0.5
        this.instructionsPanel = this.game.add.group()
        this.instructionsPanel.add(tint)
        new Rectangle(
            this.game,
            Scale.width() * 0.05,
            Scale.height() * 0.05,
            Scale.width() * 0.9,
            Scale.height() * 0.9,
            {lineWidth: 10, fillColor: colorNum.lightGray, cornerRounding: 20, group: this.instructionsPanel}
        )
        const title = Sprites.add(this.game, CampCravingsAssets.TITLE, {group: this.instructionsPanel})
        Sprites.centerInWorld(title)

        const instructionsRound = PhaserText.create(this.game, `Round ${roundNum}`, {
            y: Scale.height() * 0.15, fontPx: this.largeText, type: CampCravingsState.FONT_TYPE, group: this.instructionsPanel
        })
        Sprites.centerInWorldXOnly(instructionsRound)
        instructionsRound.visible = false

        const pawnBox = (x: number, color: string, labelText: string, lowerLabelText: string, asset: string, upperLabelText?: string):
            { box: Rectangle, pawn: Phaser.Sprite, label: Phaser.Text, lowerLabel: Phaser.Text, upperLabel: Phaser.Text } => {

            const boxWidth = Scale.width() * 0.25
            const box = new Rectangle(
                this.game,
                x,
                Scale.height() * 0.3,
                boxWidth,
                boxWidth,
                {lineColor: ColorUtils.colorHexStringToNumber(color), group: this.instructionsPanel}
            )
            box.graphics.visible = false
            const pawn = Sprites.add(this.game, asset, {group: this.instructionsPanel})
            pawn.visible = false
            Sprites.centerSpriteInSprite(box.invisibleSprite, pawn)
            pawn.y -= Scale.height() * 0.04
            const label = PhaserText.create(this.game, labelText, {
                fontPx: this.mediumText, type: CampCravingsState.FONT_TYPE, group: this.instructionsPanel
            })
            Sprites.centerSpriteInSprite(box.invisibleSprite, label)
            label.y += Scale.height() * 0.115
            label.visible = false

            const lowerLabel = PhaserText.create(this.game, lowerLabelText, {
                fontPx: this.largeText, type: CampCravingsState.FONT_TYPE, color, group: this.instructionsPanel
            })
            Sprites.centerSpriteInSprite(box.invisibleSprite, lowerLabel)
            lowerLabel.y += Scale.height() * 0.25
            lowerLabel.visible = false

            const upperLabel = PhaserText.create(this.game, upperLabelText, {
                fontPx: this.largeText, type: CampCravingsState.FONT_TYPE, color, group: this.instructionsPanel
            })
            Sprites.centerSpriteInSprite(box.invisibleSprite, upperLabel)
            upperLabel.y -= Scale.height() * 0.25
            upperLabel.visible = false
            return {box, pawn, label, lowerLabel, upperLabel}
        }
        const nonUserBox = pawnBox(Scale.width() * 0.225, COLOR.green, "Tobacco Free", roundNum === 1 ? "Computer" : "You",
            CampCravingsAssets.STATIONARY_NON_USER_PAWN)
        const nonUserPlayerBox = pawnBox(Scale.width() * 0.225, COLOR.red, "Tobacco Free", "You", CampCravingsAssets.STATIONARY_NON_USER_PAWN, "Round 2")
        const userBox = pawnBox(Scale.width() * 0.525, COLOR.red, "Tobacco User", roundNum === 1 ? "You" : "Computer", CampCravingsAssets.STATIONARY_USER_PAWN,
            "Round 1")

        const playButton = Buttons.create(
            this.game, "PLAY", () => {
                this.stopIntro()
                round.start()
            },
            {
                x: 0,
                y: Scale.height() * 0.8,
                textConfig: {type: CampCravingsState.FONT_TYPE},
                centerInWorldX: true,
                enabled: false,
                group: this.instructionsPanel,
                buttonType: ButtonType.SECONDARY
            }
        )
        playButton.button.visible = false
        playButton.text.visible = false
        if (CCUtils.debug) {
            playButton.enable()
            playButton.button.visible = true
            playButton.text.visible = true
        }

        const firstPlayerCircle = new Circle(
            this.game,
            Scale.x(0.345),
            Scale.y(0.67),
            Scale.x(0.06)
        )
        firstPlayerCircle.graphics.visible = false
        const firstPlayerNumber = PhaserText.create(this.game, "1", {
            fontPx: this.largeText, type: CampCravingsState.FONT_TYPE, group: this.instructionsPanel
        })
        firstPlayerNumber.visible = false
        Sprites.centerSpriteInSprite(firstPlayerCircle.invisibleSprite, firstPlayerNumber)

        // Show and hide the panel's contents for the audio.
        let timings
        if (roundNum === 1) {
            timings = {
                bluePawn: 2500,
                yellowPawn: 6500,
                highlightYellow: 11000,
                highlightBlue: 14000,
                firstPlayer: 19000,
                everything: 22000,
            }
            if (CCUtils.debug) {
                this.skipToRoundTwo = Buttons.create(
                    this.game,
                    "Skip Round",
                    this.startRoundTwo,
                    {y: Scale.height() * 0.85, centerInWorldX: true, buttonType: ButtonType.SECONDARY, buttonSize: ButtonSize.WIDE},
                )
                this.skipToOutro = Buttons.create(
                    this.game,
                    "Skip to outro",
                    () => this.game.state.start(TransitionState.CAMP_CRAVINGS_EXIT),
                    {y: Scale.height() * 0.95, centerInWorldX: true, buttonType: ButtonType.SECONDARY, buttonSize: ButtonSize.WIDE},
                )  
            }
        } else {
            timings = {
                bluePawn: 0,
                yellowPawn: 0,
                highlightYellow: 0,
                highlightBlue: 0,
                firstPlayer: 0,
                everything: 0,
            }
        }

        oneOffs.add(timings.bluePawn, () => {
            title.visible = false
            nonUserPlayerBox.pawn.visible = true
            nonUserPlayerBox.label.visible = true
        })

        oneOffs.add(timings.yellowPawn, () => {
            userBox.pawn.visible = true
            userBox.label.visible = true
        })

        oneOffs.add(timings.highlightYellow, () => {
            userBox.box.graphics.visible = true
            userBox.lowerLabel.visible = true
            userBox.upperLabel.visible = true
        })

        oneOffs.add(timings.highlightBlue, () => {
            userBox.box.graphics.visible = false
            userBox.lowerLabel.visible = false
            userBox.upperLabel.visible = false
            nonUserPlayerBox.box.graphics.visible = true
            nonUserPlayerBox.lowerLabel.visible = true
            nonUserPlayerBox.upperLabel.visible = true
        })

        oneOffs.add(timings.firstPlayer, () => {
            nonUserPlayerBox.box.graphics.visible = false
            nonUserPlayerBox.upperLabel.visible = false
            nonUserPlayerBox.lowerLabel.visible = false
            firstPlayerCircle.graphics.visible = true
            firstPlayerNumber.visible = true
        })

        oneOffs.add(timings.everything, () => {
            userBox.box.graphics.visible = true
            userBox.lowerLabel.visible = true
            nonUserBox.box.graphics.visible = true
            nonUserBox.pawn.visible = true
            nonUserBox.label.visible = true
            nonUserBox.lowerLabel.visible = true
            instructionsRound.visible = true
            playButton.button.visible = true
            playButton.text.visible = true
            nonUserPlayerBox.pawn.visible = false
            firstPlayerCircle.graphics.visible = false
            firstPlayerNumber.visible = false
        })

        this.introAudio.onStop.addOnce(() => {
            if (playButton.button.exists) {
                playButton.enable()
            }
        })
        oneOffs.start()
    }

    stopIntro = () => {
        if (this.skipToRoundTwo) {
            this.skipToRoundTwo.destroy()
        }
        if (this.skipToOutro) {
            this.skipToOutro.destroy()
        }
        this.instructionsPanel.destroy()
        if (this.introAudio) {
            this.introAudio.stop()
        }
    }

    setupActivities = () => {
        Sprites.add(this.game, CampCravingsAssets.BUS, {x: CampPos.busX(), y: CampPos.busY()})
        this.lake = Sprites.add(this.game, CampCravingsAssets.LAKE, {x: CampPos.lakeX(), y: CampPos.lakeY()})
        this.picnic = Sprites.add(this.game, CampCravingsAssets.PICNIC, {x: CampPos.picnicX(), y: CampPos.picnicY()})
        this.picnic.animations.add(CampCravingsState.PICNIC_1_ANIM, range(1, 5), 1)
        this.picnic.animations.add(CampCravingsState.PICNIC_2_ANIM, range(5, 8), 1)
        this.lodge = Sprites.add(this.game, CampCravingsAssets.LODGE, {x: CampPos.lodgeX(), y: CampPos.lodgeY()})
        this.lodge.animations.add(CampCravingsState.LODGE_ANIM, [1, 2], 4)
    }

    setupLabels = () => {

        this.roundText = PhaserText.create(this.game, "ROUND 1", {
            x: CampPos.roundX(), y: CampPos.roundY(), fontPx: this.mediumText, type: CampCravingsState.FONT_TYPE, color: COLOR.lightGreen
        })

        PhaserText.create(this.game, "START", {
            x: CampPos.startX(), y: CampPos.startY(), fontPx: this.mediumText, type: CampCravingsState.FONT_TYPE
        })

        PhaserText.create(this.game, "FINISH", {
            x: CampPos.stopX(), y: CampPos.stopY(), fontPx: this.mediumText, type: CampCravingsState.FONT_TYPE
        })

        PhaserText.create(this.game, "FINISH", {
            x: CampPos.stopX(), y: CampPos.stopY(), fontPx: this.mediumText, type: CampCravingsState.FONT_TYPE
        })

        PhaserText.create(this.game, "Bus stop", {
            x: CampPos.busLabelX(), y: CampPos.busLabelY(), fontPx: this.labelText, type: CampCravingsState.FONT_TYPE
        })

        PhaserText.create(this.game, "Lake", {
            x: CampPos.lakeLabelX(), y: CampPos.lakeLabelY(), fontPx: this.labelText, type: CampCravingsState.FONT_TYPE
        })

        PhaserText.create(this.game, "Lunch site", {
            x: CampPos.picnicLabelX(), y: CampPos.picnicLabelY(), fontPx: this.labelText, type: CampCravingsState.FONT_TYPE
        })

        PhaserText.create(this.game, "Nature trail", {
            x: CampPos.trailLabelX(), y: CampPos.trailLabelY(), fontPx: this.labelText, type: CampCravingsState.FONT_TYPE
        })

        PhaserText.create(this.game, "Horse trails", {
            x: CampPos.horsesLabelX(), y: CampPos.horsesLabelY(), fontPx: this.labelText, type: CampCravingsState.FONT_TYPE
        })

        PhaserText.create(this.game, "long\nloop", {
            x: CampPos.longLoopLabelX(), y: CampPos.longLoopLabelY(), fontPx: this.smallLabelText, type: CampCravingsState.FONT_TYPE, align: Align.CENTER
        })

        PhaserText.create(this.game, "short\nloop", {
            x: CampPos.shortLoopLabelX(), y: CampPos.shortLoopLabelY(), fontPx: this.smallLabelText, type: CampCravingsState.FONT_TYPE, align: Align.CENTER
        })

        PhaserText.create(this.game, "Lodge", {
            x: CampPos.lodgeLabelX(), y: CampPos.lodgeLabelY(), fontPx: this.labelText, type: CampCravingsState.FONT_TYPE, align: Align.CENTER
        })
    }

}
